import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Info, Moon, Sun, Repeat, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Reveal } from '@/components/ui/Reveal';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function EstimationTaxi() {
  useSEO({
    title: "Estimation tarif taxi conventionné | Allier - Groupe Beaulon",
    description: "Estimez le coût approximatif de votre course en taxi conventionné dans l'Allier selon les tarifs préfectoraux.",
    keywords: "tarif taxi allier, prix taxi conventionné, estimation taxi moulins"
  });

  const [distance, setDistance] = useState(10);
  const [isNight, setIsNight] = useState(false);
  const [isSundayHoliday, setIsSundayHoliday] = useState(false);
  const [allerRetour, setAllerRetour] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const calculate = async () => {
    if (!distance || distance <= 0) {
      toast.error('Veuillez saisir une distance valide.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/taxi-estimate', {
        distance_km: Number(distance),
        is_night: isNight,
        is_sunday_holiday: isSundayHoliday,
        aller_retour: allerRetour
      });
      setResult(res.data);
    } catch (err) {
      toast.error('Erreur lors du calcul. Merci de réessayer.');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10">
          <Reveal>
            <p className="badge-soft mb-3">Estimation tarif</p>
            <h1 className="h1 text-anthracite-950">Estimateur de tarif taxi conventionné</h1>
            <p className="lead mt-3 max-w-3xl">Calculez une estimation de votre course selon les tarifs en vigueur dans le département de l'Allier.</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app max-w-3xl">
          <div className="card-base p-6 sm:p-8" data-testid="taxi-estimator-form">
            <div className="flex items-start gap-3 mb-6">
              <div className="h-11 w-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                <Calculator className="h-5 w-5" />
              </div>
              <div>
                <h2 className="h3 text-anthracite-950">Paramètres du trajet</h2>
                <p className="text-sm text-muted-foreground">Renseignez la distance et les options applicables</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium">Distance estimée (km) *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={distance}
                  onChange={e => setDistance(e.target.value)}
                  className="mt-2"
                  data-testid="taxi-distance-input"
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <label className="flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm"><Moon className="h-4 w-4 text-brand-700" /> Nuit (19h-7h)</span>
                  <Switch checked={isNight} onCheckedChange={setIsNight} data-testid="taxi-night-switch" />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm"><Sun className="h-4 w-4 text-amber-500" /> Dimanche/Férié</span>
                  <Switch checked={isSundayHoliday} onCheckedChange={setIsSundayHoliday} data-testid="taxi-sunday-switch" />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm"><Repeat className="h-4 w-4 text-brand-700" /> Aller-retour</span>
                  <Switch checked={allerRetour} onCheckedChange={setAllerRetour} data-testid="taxi-ar-switch" />
                </label>
              </div>

              <Button onClick={calculate} disabled={loading} size="lg" className="w-full" data-testid="taxi-calculate-button">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Calcul...</> : <><Calculator className="h-4 w-4" /> Calculer l'estimation</>}
              </Button>
            </div>
          </div>

          {result && (
            <div className="mt-6 card-base p-6 sm:p-8 border-brand-200 bg-brand-50/40" data-testid="taxi-result">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <p className="text-sm text-anthracite-700">Estimation indicative</p>
                  <p className="text-4xl font-heading font-semibold text-anthracite-950 mt-1">
                    {result.estimation_total.toFixed(2)} €
                  </p>
                </div>
                <div className="text-xs px-3 py-2 rounded-lg bg-white border">
                  Tarif appliqué : <span className="font-medium text-anthracite-900">{result.tarif_label}</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <DetailRow label="Distance saisie" value={`${result.distance_km} km`} />
                <DetailRow label="Distance totale (A/R inclus)" value={`${result.total_km} km`} />
                <DetailRow label="Prise en charge" value={`${result.prise_en_charge.toFixed(2)} €`} />
                <DetailRow label="Tarif kilométrique" value={`${result.tarif_kilometrique.toFixed(2)} €/km`} />
                <DetailRow label="Coût kilométrique" value={`${result.cout_kilometrique.toFixed(2)} €`} />
                <DetailRow label="Minimum de course" value={`${result.minimum_course.toFixed(2)} €`} />
              </div>

              <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-4 flex gap-3">
                <Info className="h-5 w-5 text-amber-700 shrink-0" />
                <p className="text-sm text-amber-900">{result.mention}</p>
              </div>
            </div>
          )}

          {/* Lien vers le simulateur général (VSL / Ambulance) */}
          <Reveal delay={80}>
            <div className="mt-6 rounded-xl bg-anthracite-100/60 border p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-brand-700 shrink-0" />
              <p className="text-sm text-anthracite-800">
                Découvrez aussi les estimations{' '}
                <Link to="/estimation-transport" className="font-medium text-brand-700 hover:text-brand-900 underline inline-flex items-center gap-1" data-testid="taxi-to-transport-link">
                  VSL et Ambulance <ArrowRight className="h-3.5 w-3.5" />
                </Link>{' '}
                dans notre simulateur unifié Taxi / VSL / Ambulance.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-anthracite-200/50 pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-anthracite-900">{value}</span>
    </div>
  );
}
